import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  certificateFile,
  CourseParticipant,
  CourseProgress,
  CourseProgressNameType,
} from '../dataTypes';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CourseParticipantsService {
  constructor(private http: HttpClient) {}
  /**
   * Gets all participants from database
   * @returns
   */
  getParticipants(): Observable<CourseParticipant[]> {
    return this.http.get<CourseParticipant[]>('/api/CourseParticipant');
  }
  /**
   * Upserts the given course participants.
   *
   * This method sends a POST request to the server to either insert new participants
   * or update existing ones based on the provided list of course participants.
   *
   * @param courseParticipants - An array of CourseParticipant objects to be upserted.
   * @returns An Observable that emits the upserted CourseParticipant array.
   */
  upsertParticipants(courseParticipants: CourseParticipant[]): Observable<CourseParticipant[]> {
    return this.http.post<CourseParticipant[]>(
      '/api/CourseParticipant/upsertParticipants',
      courseParticipants
    );
  }


  /**
   * Updates participants in database with the use of a list CourseParticipant[] and a userId for eventlog, to see who made the change
   * @param courseParticipants
   * @returns
   */
  updateParticipants(courseParticipants: CourseParticipant[]): Observable<CourseParticipant> {
    return this.http.post<CourseParticipant>(
      '/api/CourseParticipant/updateParticipants',
      courseParticipants
    );
  }

  /**
   * Updates a single participant in database with the use of a CourseParticipant and a userId for eventlog, to see who made the change
   * @param courseParticipant
   * @returns
   */
  updateParticipant(courseParticipant: CourseParticipant): Observable<CourseParticipant> {
    return this.http.put<CourseParticipant>(
      '/api/CourseParticipant',
      courseParticipant
    );
  }

  /** If participant has multiple statuses on a participation, this function will delete the latest one by using ID of Participant and a userId for eventlog, to see who made the change
   * @param id
   * @returns
   */
  deleteCourseProgress(id: number): Observable<CourseParticipant> {
    return this.http.delete<CourseParticipant>(
      '/api/CourseParticipant/DeleteCourseProgress/' + id
    );
  }


  /**
   * Deletes all statuses and the participant record for a given participant ID.
   * @param id The ID of the participant.
   * @returns An Observable indicating the result of the operation.
   */
  deleteAllHistory(id: number){
    return this.http.delete(
      `/api/CourseParticipant/deleteAllHistory/${id}`,
      { responseType: 'text' }
    );
  }


  /**
   * deletes a certificate by courseprogressId
   * @param courseprogressId
   * @returns
   */
  deleteCertificate(courseProgressId: number): Observable<any> {
    return this.http.delete<any>(
      `/api/CourseParticipant/deleteCertificate/${courseProgressId}`
    );
  }

  /**
   * Uploads a certificate for a participant
   * @param participantId The ID of the participant
   * @param formData The form data containing the certificate file
   * @returns An Observable indicating the result of the operation
   */
  uploadCertificate(
    participantId: number,
    formData: FormData
  ): Observable<any> {
    return this.http.post<any>(
      `/api/CourseParticipant/uploadCertificate/${participantId}`,
      formData
    );
  }

  /**
   * Returns the certificate of a user by providing users ID and the courseProgressId of which holds the certificate
   * @param courseProgressId
   * @returns
   */
  requestCertificate(courseProgressId: number): Observable<certificateFile> {
    return this.http.get<certificateFile>(
      `/api/courseParticipant/requestCertificate/${courseProgressId}`
    );
  }


  getCompletioDate(id: number): Observable<void> {
    return this.http.put<void>(
      `/api/CourseParticipant/getCompletionDate/${id}`,
      {  }
    );
  }
}
